<script lang="ts" setup>
  import {
    ref,
    reactive,
    watch,
    computed,
    type PropType,
    onMounted,
    onUnmounted,
    inject,
    nextTick,
    defineComponent,
    useTemplateRef,
    watchEffect,
    shallowRef
  } from 'vue'
  import AppImage from '@/components/AppImage.vue'
  import AppSwitch from '@/components/AppSwitch.vue'
  import { Navigation } from 'swiper/modules'
  import { Swiper, SwiperSlide } from 'swiper/vue'
  import 'swiper/css'
  import 'swiper/css/navigation'
  // import type { Image } from '@/types/image'
  import ToastService from '@/components/AppToast/toast'
  import type { ToastServiceType } from '@/components/AppToast/toast'
  import { ElAvatar, ElTooltip } from 'element-plus'
  import { ElMention, type MentionOption } from '@/components/ElementMention'
  import { useMentionList } from '@/hooks/useMentionList'
  import type { MentionCtx } from 'element-plus/es/components/mention/src/types'
  import VideoPlayer from '@/components/VideoPlayer/index.vue'
  import httpApi from '@/http/api'
  import { filterTopicsByMedia, mediaSupportsWantableTopic } from './topicMediaCompat'

  defineComponent({
    components: {
      ElMention
      // ElAvatar
    }
  })
  // 添加埋点相关导入
  import noMentionImage from '@/assets/images/no-mention.png'

  const $toast = inject<ToastServiceType>('$toast')

  interface Challenge {
    title?: string
    wantable?: number
    uniqueTopicId?: string
    banners?: { id: string; image: string; sidebarImage: string }[]
    allowPostWantByTopicIds?: []
  }

  const props = defineProps({
    modelValue: {
      type: Map as PropType<Map<string, any>>,
      required: true
    },
    title: {
      type: String,
      required: false
    },
    hasNotBack: {
      type: Boolean,
      default: false
    },
    topicIdMsg: {
      type: String,
      default: ''
    },
    titleMsg: {
      type: String,
      default: ''
    },
    descriptionMsg: {
      type: String,
      default: ''
    },
    challenges: {
      type: Array as PropType<Challenge[]>,
      default: () => []
    },
    tabType: {
      type: String,
      default: ''
    },
    isShow: {
      type: Boolean,
      default: false
    }
  })
  const emit = defineEmits(['input', 'back', 'select', 'checkedMore', 'backConfigSettings'])
  const checkedMaxLength: number = inject('checkedMaxLength') || 9
  const containerRef = ref<HTMLDivElement>()
  const titleVal = ref(props.titleMsg)
  const description = ref(props.descriptionMsg)
  const originalDescription = ref(description.value)
  const originalMentionSelection = shallowRef<{ start: number | null; end: number | null }>({
    start: null,
    end: null
  })
  const topicId = ref(props.topicIdMsg)
  // const topicId = ref('123')
  const isFirstInit = ref(true)
  const wantable = ref(0)
  const activityType = ref(0)
  const allowPostWantByTopicIds = ref([''])
  const videoPlayerRef = ref<InstanceType<typeof VideoPlayer>[]>()

  const showPrompt = ref(localStorage.getItem('postHidePrompt') !== '1')
  const enablePurchase = reactive({
    enable: false,
    disabledSwitch: false,
    isManualModifed: false,
    isVisible: false
  })
  const desLength = computed(() => {
    return description.value.length
  })
  const titleLen = computed(() => {
    return titleVal.value.length
  })

  const titleMaxLength = 50
  const maxLength = 500
  const configSettings = ref({
    associateOriginalImage: true
  })
  const canPost = ref(true)

  // 检查当前 topicId 是否存在于 challenges 列表中
  const isTopicInChallenges = computed(() => {
    if (!topicId.value) return false
    return props.challenges.some(item => item.uniqueTopicId === topicId.value)
  })

  const postType = computed(() => {
    if (props.modelValue && typeof props.modelValue.forEach === 'function') {
      let hasVideo = false
      props.modelValue.forEach((item: any) => {
        if (item && item.target === 'video') {
          hasVideo = true
        }
      })
      return hasVideo ? 'video' : 'image'
    }
    return 'image'
  })

  const {
    insertMentionUser,
    modifyMentionList,
    shouldRemoveWholeMention,
    getMentionList,
    resetMentionList
  } = useMentionList()

  // @提及相关状态
  const mentionUsers = ref<any[]>([])
  const mentionLoading = ref(false)
  const showMentionDropdown = ref(true)
  const mentionSearchKeyword = ref('')
  const currentPage = ref(1)
  const mentionTextarea = useTemplateRef<InstanceType<typeof ElMention>>('mentionTextarea')

  // 处理@按钮点击
  const handleAtButtonClick = () => {
    if (!mentionTextarea.value?.input?.textarea) return

    // 获取当前光标位置
    const textarea = mentionTextarea.value.input.textarea
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const currentText = description.value

    // 在光标位置插入@符号
    const newText = currentText.substring(0, start) + '@' + currentText.substring(end)
    description.value = newText
    modifyMentionList({
      oldValue: originalDescription.value,
      value: newText,
      cursor: start + 1,
      originalSelection: originalMentionSelection.value
    })
    originalDescription.value = newText

    // 设置光标位置到@符号后面
    nextTick(() => {
      textarea.focus()
      textarea.setSelectionRange(start + 1, start + 1)
      originalMentionSelection.value = {
        start: start + 1,
        end: start + 1
      }
      // 触发搜索
      handleMentionSearch('')
    })
  }
  // 删除键盘事件监听 - el-mention已经处理了键盘输入

  // 处理输入事件 - 更新description值并发送给父组件
  const handleMentionInput = (value: string, triggerBy: string) => {
    // 更新description值
    if (value.length > maxLength) {
      description.value = value.substring(0, maxLength)
    } else {
      description.value = value
    }

    // 发送给父组件
    emit('input', {
      type: 'description',
      value: description.value
    })
    nextTick(() => {
      if (triggerBy !== 'select') {
        if (!mentionTextarea.value?.input?.textarea) {
          return
        }
        const cursor = mentionTextarea.value.input.textarea.selectionEnd
        modifyMentionList({
          oldValue: originalDescription.value,
          value: value,
          cursor: cursor,
          originalSelection:
            triggerBy === 'whole-remove'
              ? { start: cursor - (originalDescription.value.length - value.length), end: cursor }
              : originalMentionSelection.value
        })
      }
      originalDescription.value = value
    })
  }

  const handleMentionInputSelect = (selectionStart: number | null, selectionEnd: number | null) => {
    originalMentionSelection.value = {
      start: selectionStart,
      end: selectionEnd
    }
  }

  // 监听用户列表变化
  watch(mentionUsers, newVal => {
    if (newVal.length > 0) {
      showMentionDropdown.value = true
    }
  })

  watch(
    [() => props.modelValue, () => wantable.value],
    ([newModelValue, newWantable]) => {
      const enable = [...newModelValue.values()].some(item => item.customStatus === 1)
      // 如果被用户手动关闭
      if (newWantable === 1) {
        enablePurchase.enable = true
        enablePurchase.disabledSwitch = true
        enablePurchase.isManualModifed = false
        enablePurchase.isVisible = true
      } else {
        if (enable) {
          if (!enablePurchase.isManualModifed) {
            enablePurchase.enable = true
          }
          enablePurchase.isVisible = true
          enablePurchase.disabledSwitch = false
        } else {
          enablePurchase.isVisible = false
        }
      }
    },
    {
      deep: true
    }
  )

  // 接口完整列表；下拉展示 filtered（任意素材支持「想要」即展示）
  const displayChallenges = computed(() => {
    const mediaList = Array.from(props.modelValue.values()) as Record<string, unknown>[]
    return filterTopicsByMedia(props.challenges, mediaList)
  })

  const getMediaItemOpacityStyle = (item: any): string => {
    // 优先判断 canPost 状态，如果检查不通过，所有图片都显示半透明
    if (!canPost.value) {
      return 'opacity: 0.4'
    }

    if (props.tabType === 'loacl') {
      if (wantable.value === 1 && activityType.value === 1) {
        return 'opacity: 0.4'
      } else {
        return 'opacity: 1'
      }
    } else {
      if (wantable.value === 1) {
        if (!topicId.value) {
          return 'opacity: 1'
        }
        return mediaSupportsWantableTopic(item, topicId.value, allowPostWantByTopicIds.value)
          ? 'opacity: 1'
          : 'opacity: 0.4'
      }
      return 'opacity: 1'
    }
  }

  const inputDes = (type: string) => {
    if (type === 'title') {
      if (titleVal.value.length > titleMaxLength) {
        titleVal.value = titleVal.value.substring(0, titleMaxLength)
      }
    } else {
      if (desLength.value > maxLength) {
        description.value = description.value.substring(0, maxLength)
      }
    }
    emit('input', {
      type: type,
      value: type === 'title' ? titleVal.value : description.value
    })
  }

  const deleteImage = (key: string) => {
    props.modelValue.delete(key)

    // 删除视频的原图时，需要反向重置下关联原图按钮
    if (postType.value === 'video') {
      configSettings.value.associateOriginalImage = false
    }

    let res: any =
      props.challenges.length > 0 &&
      props.challenges.find(item => item.uniqueTopicId === topicId.value)
    const equleNum = getEquleNum(false, res?.allowPostWantByTopicIds || [])
    let params = {
      topicId: topicId.value,
      title: res?.title || '',
      equleNum: equleNum,
      want: res?.wantable || 0,
      key: key,
      activityType: activityType.value || 0,
      allowPostWantByTopicIds: res?.allowPostWantByTopicIds || []
    }
    emit('select', params)
  }
  const challengeShow = ref(false)
  const toggleChallenge = () => {
    challengeShow.value = false
  }

  // 符合当前选中挑战的素材张数（与 App getWantedNum 语义对齐：wantableTopics 命中即计 1）
  const getEquleNum = (_isCheckCanPost: boolean, allowPostWantByTopicIds: Array<string>) => {
    let equleNum = 0
    props.modelValue.forEach((item: any) => {
      if (!topicId.value) return
      if (mediaSupportsWantableTopic(item, topicId.value, allowPostWantByTopicIds)) {
        equleNum++
      }
    })
    return equleNum
  }

  const checkCanPost = async (wantable: boolean) => {
    if (wantable) {
      const firstItem = Array.from(props.modelValue.values())[0]
      if (!firstItem.itemId) return
      const params = {
        itemId: firstItem?.itemId,
        generateId: firstItem?.generateId,
        topicId: topicId.value
      }
      const res: any = await httpApi.postPreCheck(params)
      if (res.code === 200) {
        canPost.value = res.data.status === 0
        if (!canPost.value) {
          ToastService({
            message: res.data.reason,
            type: 'warning'
          })
        }
      }
    } else {
      // 当 wantable 为假时（没有选中挑战或清除了挑战），重置 canPost 状态
      canPost.value = true
    }
  }

  watch(
    [() => props.modelValue, () => topicId.value, () => props.challenges],
    async () => {
      if (!props.challenges.length) return
      const item: any =
        props.challenges.length > 0 &&
        props.challenges.find(item => item.uniqueTopicId === topicId.value)

      // 如果 topicId 有值但在 challenges 中找不到，则重置为空
      if (topicId.value && !item) {
        topicId.value = ''
        wantable.value = 0
        activityType.value = 0
        allowPostWantByTopicIds.value = []
        return // 重置后会重新触发 watch，所以这里直接返回
      }

      wantable.value = item?.wantable ?? wantable.value ?? 0
      activityType.value = item?.activityType
      allowPostWantByTopicIds.value = item?.allowPostWantByTopicIds || []
      const equleNum = getEquleNum(canPost.value, item?.allowPostWantByTopicIds || [])

      await checkCanPost(item?.wantable)

      let params = {
        topicId: topicId.value,
        title: item?.title || '',
        equleNum: equleNum,
        want: wantable.value,
        key: 0,
        canPost: canPost.value,
        activityType: activityType.value || 0,
        allowPostWantByTopicIds: item?.allowPostWantByTopicIds || []
      }
      emit('select', params)
    },
    { immediate: true, deep: true }
  )

  watchEffect(() => {
    if (postType.value === 'video') {
      emit('backConfigSettings', configSettings.value)
    }
  })

  // 发布时默认带一个想要话题
  watchEffect(() => {
    if (!props.isShow) return
    if (
      ((postType.value === 'image' && props.modelValue.size === 1) || postType.value === 'video') &&
      !topicId.value &&
      isFirstInit.value
    ) {
      const firstItem = Array.from(props.modelValue.values())[0]
      if (firstItem.wantableTopics && firstItem.wantableTopics.length > 0) {
        wantable.value = 1
        topicId.value = firstItem.wantableTopics[0].uniqueTopicId
      }
    }
    isFirstInit.value = false
  })

  const checkedTopicId = (item: any) => {
    const mediaList = Array.from(props.modelValue.values())
    if (
      props.tabType !== 'loacl' &&
      item?.wantable === 1 &&
      mediaList.length > 0 &&
      item.uniqueTopicId
    ) {
      const allowed = item.allowPostWantByTopicIds || []
      let compatible = 0
      mediaList.forEach((m: any) => {
        if (mediaSupportsWantableTopic(m, item.uniqueTopicId, allowed)) compatible++
      })
      if (compatible < mediaList.length) {
        ToastService({
          message: `置灰${postType.value === 'video' ? '视频' : '图片'}无法发布在当前挑战，请删除后重试`,
          backgroundColor: '#707070',
          type: 'warning'
        })
      }
    }
    topicId.value = item?.uniqueTopicId
  }

  // 叉掉选中的活动
  const unSelectTopic = (item: any) => {
    topicId.value = ''
    wantable.value = 0
    activityType.value = 0
    allowPostWantByTopicIds.value = []
    emit('select', {
      topicId: '',
      title: item?.title || '',
      equleNum: 0,
      want: 0,
      key: 0,
      activityType: activityType.value || 0,
      allowPostWantByTopicIds: item?.allowPostWantByTopicIds || []
    })
  }

  // ----------------------------------@相关的放在最后，便于维护和抽离----------------------------------------------------
  import {
    type MentionPanelState,
    type MentionSearchParams,
    type MentionSelectParams,
    handleMentionSearch as handleMentionSearchUtil,
    handleMentionSelect as handleMentionSelectUtil,
    getAccountLevel,
    loadMoreUsers
  } from '@/views/AppContentDetail/mentionPanelUtils'

  // 防抖定时器
  let searchTimeout: NodeJS.Timeout | null = null
  const mentionCursor = ref('')
  const mentionHasMore = ref(true)
  const showEmptyState = ref(false) // 空态显示
  const mentionLoadingMore = ref(false) // 翻页加载loading
  const mentionSessionId = ref('') // 会话ID
  import { AccountLevelImage } from '@/utils/constants'
  import Avatar from '@/components/Avatar/index.vue'
  import { unescape } from 'querystring'

  // 创建面板状态对象
  const mentionPanelState: MentionPanelState = {
    mentionLoading,
    mentionLoadingMore,
    mentionHasMore,
    showEmptyState,
    mentionUsers,
    mentionCursor,
    sessionId: mentionSessionId
  }

  // 创建搜索参数对象
  const mentionSearchParams: MentionSearchParams = {
    currentPage,
    mentionSearchKeyword,
    $toast
  }

  // 处理@提及搜索
  const handleMentionSearch = async (query: string) => {
    await handleMentionSearchUtil(
      query,
      mentionPanelState,
      mentionSearchParams,
      ref(searchTimeout),
      loadMoreUsersWrapper
    )
  }
  // 翻页加载更多用户包装函数
  const loadMoreUsersWrapper = async () => {
    await loadMoreUsers(mentionPanelState, mentionSearchParams)
  }
  // 处理@提及选择
  const handleMentionSelect = (user: MentionOption, mentionCtx: MentionCtx, offset: number) => {
    const params: MentionSelectParams = {
      user,
      isDuplicate: getMentionList().some(item => item.userId === user.userId),
      $toast,
      pageName: 'generator_page',
      scene: 'post' // 发帖区场景
    }
    handleMentionSelectUtil(params)
    const newMentionItem = {
      startIndex: mentionCtx.prefixIndex,
      length: user.name.length + 1,
      userId: user.userId
    }
    insertMentionUser(newMentionItem, offset)
  }
  //  ----------- 翻页相关 --------------------------------------------------------------
  // 滚动监听函数
  const handleScroll = () => {
    const scrollContainer = document.querySelector('.custom-mention-dropdown .el-scrollbar__wrap')
    if (scrollContainer) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer
      // 距离面板底部30px
      if (scrollHeight - scrollTop - clientHeight < 30) {
        loadMoreUsersWrapper()
      }
    }
  }
  const handleEnablePurchase = (value: boolean) => {
    enablePurchase.isManualModifed = true
  }
  // 处理弹窗显示状态变化
  const handleMentionVisibleChange = (visible: boolean) => {
    showMentionDropdown.value = visible

    if (visible) {
      // 弹窗显示时，确保滚动监听已添加
      nextTick(() => {
        setTimeout(() => {
          addScrollListener()
        }, 100)
      })
    }
  }

  const getHidePrompt = () => {
    return !showPrompt.value
  }

  const getEnablePurchase = () => {
    return enablePurchase.isVisible ? enablePurchase.enable : undefined
  }
  // 添加滚动监听
  const addScrollListener = () => {
    const selectors = [
      '.custom-mention-popper .el-scrollbar__wrap',
      '.custom-mention-popper .el-scrollbar__view',
      '.custom-mention-popper .el-select-dropdown__wrap',
      '.custom-mention-popper .el-popper__content'
    ]

    let scrollContainer = null
    for (const selector of selectors) {
      scrollContainer = document.querySelector(selector)
      if (scrollContainer) {
        break
      }
    }

    if (scrollContainer) {
      // 移除之前的监听器
      scrollContainer.removeEventListener('scroll', handleScroll)
      // 添加新的监听器
      scrollContainer.addEventListener('scroll', handleScroll)
    }
  }
  // ----------------------------------------------------------------------------------------------

  onMounted(() => {
    document.addEventListener('click', toggleChallenge)
    document.querySelector('#post_dialog')?.addEventListener('click', toggleChallenge)
  })

  onUnmounted(() => {
    document.removeEventListener('click', toggleChallenge)
    document.querySelector('#post_dialog')?.removeEventListener('click', toggleChallenge)
  })

  const jumpCreateUpgrade = () => {
    window.open('/createUpgrade', '_blank')
  }

  defineExpose({
    getMentionList,
    resetMentionList,
    getHidePrompt,
    getEnablePurchase
  })
</script>
<template>
  <div class="checked-result">
    <h4 class="checked-list-title font-bold">
      <a v-if="!hasNotBack" class="go-back hitems-arrow-left" @click="emit('back')"></a>
      发布帖子
    </h4>
    <div class="checked-result-container">
      <swiper
        :key="postType"
        style="--swiper-navigation-size: 2.6rem"
        :slidesPerView="4"
        :spaceBetween="10"
        :pagination="{ clickable: true }"
        :navigation="true"
        :modules="[Navigation]"
        class="mySwiper"
      >
        <swiper-slide v-for="[key, value] in Array.from(props.modelValue)" :key="key">
          <div :style="getMediaItemOpacityStyle(value)" class="checked-item">
            <AppImage
              :src="value.imageResize512Url"
              :fullscreen="value.url"
              alt=""
              v-if="value.target === 'image'"
            />
            <VideoPlayer
              v-else
              ref="videoPlayerRef"
              :itemData="value"
              idPrefix="post-video"
              :isShowControls="false"
              :isShowFullScreen="false"
              :cssFullscreen="true"
              :clickFullscreen="true"
            />
            <div
              v-if="props.modelValue.size > 1 && value.target !== 'video'"
              class="checked-item-delete-container"
            >
              <a @click="deleteImage(key)" class="checked-item-delete hitems-guanbi"></a>
            </div>
          </div>
        </swiper-slide>
        <swiper-slide v-if="props.modelValue.size < checkedMaxLength && postType !== 'video'">
          <div class="checked-item checked-item-add" @click="emit('checkedMore')">
            <span class="hitems-add"></span>
          </div>
        </swiper-slide>
      </swiper>
      <div class="publish-description-container">
        <span class="publish-description-count">{{ desLength }}/{{ maxLength }}</span>
        <span class="publish-description-count title">{{ titleLen }}/{{ titleMaxLength }}</span>
        <input
          v-model="titleVal"
          @input="inputDes('title')"
          :placeholder="$t('appPostMessage.enter_title')"
          class="publish-title"
        />
        <el-mention
          ref="mentionTextarea"
          v-model="description"
          :options="mentionUsers"
          :filter-option="false"
          :prefix="['@']"
          :trigger="['@']"
          type="textarea"
          :rows="4"
          resize="none"
          :maxlength="maxLength"
          popper-class="custom-mention-dropdown"
          class="publish-description"
          :placeholder="$t('appPostMessage.share_with_us')"
          @search="handleMentionSearch"
          @select="handleMentionSelect"
          @visible-change="handleMentionVisibleChange"
          @input="handleMentionInput"
          :whole="true"
          :check-is-whole="shouldRemoveWholeMention"
          @input-select="handleMentionInputSelect"
        >
          <!-- 自定义标签内容 -->
          <template #label="{ item }">
            <div v-if="!item.isEmpty" class="mention-item">
              <Avatar
                :src="item.avatarUrl"
                :nickname="item.name"
                :tags="item.tags"
                :userId="item.uuid"
                size="small"
                :clickable="false"
                class="mention-avatar"
              />
              <span class="user-name">{{ item.name }}</span>
            </div>
          </template>
          <template #empty-placeholder>
            <div
              style="
                text-align: center;
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
              "
            >
              <img
                :src="noMentionImage"
                alt="No results"
                style="width: 96px; height: 96px; margin-bottom: 16px"
              />
              <p style="color: #999; font-size: 14px; margin: 0; white-space: nowrap">
                未找到相关创作者，尝试输入其它内容
              </p>
            </div>
          </template>
        </el-mention>
        <transition name="fade">
          <div class="publish-types" v-if="props.title">
            <span class="publish-type"># {{ props.title }}</span>
            <!-- @按钮 -->
            <span
              @click.stop="handleAtButtonClick"
              style="margin-left: 1.2rem"
              class="publish-type publish-select"
            >
              @
            </span>
          </div>
          <div
            @click.stop="challengeShow = !challengeShow"
            :class="['publish-types', { 'publish-select': topicId }]"
            v-else
          >
            <template v-for="item in props.challenges" :key="item.uniqueTopicId">
              <span v-if="topicId === item.uniqueTopicId" class="publish-type publish-select">
                # {{ item.title }}
                <a
                  @click.stop="() => unSelectTopic(item)"
                  class="publish-select-clear hitems-guanbi"
                ></a>
              </span>
            </template>
            <!-- 当 topicId 为空或者 topicId 不在 challenges 列表中时，显示添加挑战赛 -->
            <span v-if="!topicId || !isTopicInChallenges" class="publish-type publish-select">
              # 添加挑战赛
            </span>
            <!-- @按钮 -->
            <span
              @click.stop="handleAtButtonClick"
              style="margin-left: 1.2rem"
              class="publish-type publish-select"
            >
              @
            </span>
            <div v-if="challengeShow" class="publish-select-dropdown">
              <div class="dropdown-items">
                <div
                  class="dropdown-item"
                  v-for="item in displayChallenges"
                  :key="item.uniqueTopicId"
                  @click="checkedTopicId(item)"
                >
                  <span
                    class="dropdown-item-img"
                    :style="{
                      'background-image': `url(${item.banners?.[0].sidebarImage || item.banners?.[0].image})`
                    }"
                  ></span>
                  <span class="dropdown-item-title"># {{ item.title }}</span>
                  <span v-if="item.wantable === 1" class="dropdown-item-want">
                    <img class="want_icon" src="@/assets/images/ticket.webp" width="32" />
                    想要
                  </span>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <div class="settings-container">
        <div class="settings-item-title">更多设置</div>
        <div class="settings-item" v-if="postType === 'video'">
          <div class="settings-item-container">
            <span class="settings-item-label">关联原图发布</span>
            <el-tooltip placement="top" popper-class="create-upgrade-popover">
              <div class="question-icon"></div>
              <template #content>
                <div class="create-upgrade-tip">
                  启用关联原图发布时，使用造好物模型生成的图片，在发布后，您的原图会在作品的第二页展示
                </div>
              </template>
            </el-tooltip>
          </div>
          <AppSwitch
            v-model="configSettings.associateOriginalImage"
            switchClass="settings-switch"
          />
        </div>
        <div class="settings-item" v-if="enablePurchase.isVisible">
          <div class="settings-item-container">
            <span class="settings-item-label">作品支持单件购买</span>
            <el-tooltip
              placement="top"
              popper-class="create-upgrade-popover"
              popper-style="padding-right: 0"
            >
              <div class="question-icon"></div>
              <template #content>
                <div class="create-upgrade-tip">
                  现在起您的每件作品都支持购买， 详见 🔗
                  <span class="create-upgrade-link" @click.stop="jumpCreateUpgrade">
                    造好物模式全新升级>
                  </span>
                </div>
              </template>
            </el-tooltip>
          </div>
          <AppSwitch
            v-model="enablePurchase.enable"
            :disabled="enablePurchase.disabledSwitch"
            @update:modelValue="handleEnablePurchase"
            switchClass="settings-switch"
          />
        </div>
        <div class="settings-item">
          <span class="settings-item-label">公开描述词</span>
          <AppSwitch v-model="showPrompt" switchClass="settings-switch" />
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="stylus" scoped>
  @import './CheckedList.styl';
  // @提及容器
  .mention-container {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 0.8rem;
  }


  // el-mention 自定义样式
  :deep(.el-mention) {
    flex: 1;
    .el-textarea__inner {
      border-radius: 0.4rem;
      font-size: 1.05rem;
      line-height: 1.5;
      resize: vertical;
      min-height: 10rem;
      background: inherit;
      box-shadow: none;
      color: #333;
      padding: 5px 0;

      &::-webkit-scrollbar {
        display: none !important;
        width: 0 !important;
      }

      &:focus {
        border-color: #409eff;
        outline: none;
      }
    }

    // 隐藏el-mention自带的字数统计
    .el-input__count {
      display: none !important;
    }

    // @提及高亮样式 - 下拉面板中的选项
    .el-mention__suggestion-item {
      color: #054077 !important;
      font-weight: bold !important;
    }

    // @提及高亮样式 - 输入框中已选中的mention
    .el-mention__mention,
    .el-mention__input .el-mention__mention,
    .el-textarea__inner .el-mention__mention,
    .el-mention__input .mention-tag,
    .el-textarea__inner .mention-tag {
      color: #054077 !important;
    }
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }


  // 加载更多
  .loading-more {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.2rem;
    color: #909399;
    font-size: 1.2rem;
    border-top: 1px solid #f5f5f5;

    .loading-spinner {
      width: 1.2rem;
      height: 1.2rem;
      border: 2px solid #e4e7ed;
      border-top: 2px solid #409eff;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-right: 0.6rem;
    }
  }

  // 没有更多
  .no-more {
    text-align: center;
    color: #c0c4cc;
    font-size: 1.2rem;
    padding: 1.2rem;
    border-top: 1px solid #f5f5f5;
  }

  // 空状态
  .mention-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
    color: #c0c4cc;
    font-size: 1.4rem;

    .empty-text {
      text-align: center;
    }
  }

  // 旋转动画
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  // contenteditable输入框样式
  .publish-description {
    min-height: 10rem;
    border: 1px solid #dcdfe6;
    border-radius: 0.4rem;
    padding: 0.8rem 1.2rem;
    font-size: 1.4rem;
    line-height: 1.5;
    outline: none;
    white-space: pre-wrap;
    word-wrap: break-word;
    resize: vertical;
    background: #ffffff;
    display: block;

    &:focus {
      border-color: #409eff;
    }

    &:empty:before {
      content: attr(data-placeholder);
      color: #c0c4cc;
      pointer-events: none;
    }

    // @提及高亮样式
    :deep(.mention-highlight) {
      color: #054077 !important;
      font-weight: bold !important;
      background-color: rgba(5, 64, 119, 0.1);
      border-radius: 0.2rem;
      padding: 0.1rem 0.2rem;
      cursor: default;
      user-select: none;
    }
  }
</style>
<style lang="stylus">
  .settings-item-tooltip.tooltip-content {
    width: 18.8rem;
    background-color: rgb(101, 101, 101)
    padding: 0.8rem 0 1.2rem 0.8rem;
    border-radius: 0.8rem;
    &[data-placement='top']:after {
      border: 1rem solid transparent;
      bottom: -1.9rem;
      border-top-color: rgb(101, 101, 101);
    }
  }
  .el-tooltip.create-upgrade-popover {
    --el-text-color-primary: rgb(101, 101, 101);
    width: 18.8rem;
    padding: 0.8rem 0.8rem 1.2rem;
    border-radius: 0.8rem;
    &.is-dark {
      border: none;
    }
  }
</style>
